import { z } from 'zod';
export const PlanTierSchema = z.enum(['starter', 'pro', 'power']);
// `past_due` is set when Stripe's renewal charge failed and Smart Retries
// are in progress. Stripe's own subscription-overview docs recommend
// keeping access during this window (it's the de-facto grace period), but
// the UI should clearly signal it so the customer can fix their card.
// Access is only revoked when status flips to `unpaid` or `canceled`,
// which the cache models by deleting the user_plan record entirely (so
// downstream code sees "no active plan", i.e. PAYMENT_REQUIRED).
export const QuotaStateValueSchema = z.enum(['available', 'overage', 'depleted', 'past_due']);
export const QuotaStateSchema = z.object({
    planTier: PlanTierSchema,
    tokensRemaining: z.number().int(),
    tokensTotal: z.number().int(),
    cycleStartDate: z.string().datetime(),
    cycleEndDate: z.string().datetime(),
    overageEnabled: z.boolean(),
});
export const TokenDebitRequestSchema = z.object({
    userId: z.string(),
    amount: z.number().int().positive(),
    idempotencyKey: z.string().uuid(),
    reason: z.string(),
});
export const PlanLoaderStateSchema = z.object({
    plan: PlanTierSchema,
    tokenBalance: z.number().int().nonnegative(),
    monthlyAllocation: z.number().int().nonnegative(),
    cycleEndDate: z.string().datetime(),
    overageEnabled: z.boolean(),
    quotaState: QuotaStateValueSchema,
    // A scheduled (future) plan change carried through from the cache so /status
    // can surface it. Both null when nothing is pending. See UserPlanCache.
    scheduledPlan: PlanTierSchema.nullable().default(null),
    scheduledChangeAt: z.string().datetime().nullable().default(null),
});
export const StatusResponseDataSchema = z.object({
    plan: PlanTierSchema.nullable(),
    tokenBalance: z.number().int().nonnegative(),
    monthlyAllocation: z.number().int().nonnegative(),
    cycleResetDate: z.string().datetime().nullable(),
    overageEnabled: z.boolean().default(false),
    quotaState: QuotaStateValueSchema.default('available'),
    // A scheduled (future) plan change — the user keeps `plan` until
    // `scheduledChangeAt`, then switches to `scheduledPlan`. Both null when no
    // change is pending. `.default(null)` keeps older API responses parseable.
    scheduledPlan: PlanTierSchema.nullable().default(null),
    scheduledChangeAt: z.string().datetime().nullable().default(null),
});
export const UsageDailyPointSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    value: z.number().int().nonnegative(),
});
export const UsageResponseDataSchema = z.object({
    generationsThisCycle: z.number().int().nonnegative(),
    successRate: z.number().min(0).max(1).nullable(),
    mostUsedPreset: z.string().nullable(),
    hasHistory: z.boolean(),
    dailySeries: z.array(UsageDailyPointSchema).default([]),
});
export const UserPlanCacheSchema = z.object({
    plan: PlanTierSchema,
    monthlyAllocation: z.number().int().positive(),
    cycleStart: z.string().datetime(),
    cycleEnd: z.string().datetime(),
    stripeCustomerId: z.string(),
    stripeSubscriptionId: z.string(),
    // The base (non-metered) subscription_item id captured at checkout-complete
    // time. Optional for backward compat with records written before this field
    // existed. When present, changePlan prefers matching by this id over
    // product metadata / env price IDs — it's the strongest identifier because
    // it doesn't drift with price archival/recreation in the Stripe dashboard.
    baseSubscriptionItemId: z.string().optional(),
    // Stamp written by writeCachedPlan() after any successful change-plan
    // operation and by reconcileWithStripe() on every read-path heartbeat.
    // plan-resolver uses this to decide whether to fire a background reconcile
    // when KV+Redis agree but neither was refreshed against Stripe recently.
    // Optional so old records (missing this field) still parse; the resolver
    // treats `undefined` as "needs reconciliation".
    lastReconciledAt: z.string().datetime().optional(),
    // Stripe subscription.status, persisted by handleSubscriptionUpdated and
    // refreshed by reconcileWithStripe. Only `active` and `past_due` should
    // ever land in the cache — AgentBrush deliberately does not use trials
    // (no `trial_period_days` anywhere), and terminal statuses (`unpaid`,
    // `paused`, `incomplete_expired`, `canceled`, AND `trialing` if it
    // somehow appears) clear the record via handleSubscriptionDeleted-style
    // cleanup. The `past_due` value lets plan-resolver surface a 'past_due'
    // quotaState so the UI can warn the customer their card failed without
    // locking them out mid-grace-period.
    subscriptionStatus: z.enum(['active', 'past_due']).optional(),
    // `cancel_at_period_end` from Stripe — true when the customer scheduled
    // their subscription to cancel at the end of the current billing period
    // (typically via the Customer Portal). The plan still grants entitlement
    // until `cancelAt`, but the UI should show "Your plan ends on …".
    cancelAtPeriodEnd: z.boolean().optional(),
    // ISO timestamp of `cancel_at` when `cancelAtPeriodEnd` is true.
    cancelAt: z.string().datetime().optional(),
    // A SCHEDULED plan change — a Stripe subscription *schedule* (phased),
    // DISTINCT from cancel_at_period_end. Set when a downgrade is scheduled: the
    // user keeps their current (paid) `plan` until `scheduledChangeAt`, then the
    // schedule flips them to `scheduledPlan`. DISPLAY-ONLY: entitlement is always
    // driven by `plan`, never by `scheduledPlan`. Cleared when the change applies
    // (webhook), is canceled (keep-current), or is superseded by another change.
    scheduledPlan: PlanTierSchema.optional(),
    scheduledChangeAt: z.string().datetime().optional(),
});
export const CheckoutRequestSchema = z.object({
    planTier: PlanTierSchema,
    email: z.string().email(),
});
export const CheckoutResponseSchema = z.object({
    clientSecret: z.string(),
});
export const ChangePlanRequestSchema = z.object({
    planTier: PlanTierSchema,
    // Optional payment method (pm_...) collected via a SetupIntent in the upgrade
    // checkout. When present, the change-plan route sets it as the subscription's
    // default payment method before the prorated charge, so the upgrade is billed
    // to the card the customer just entered (rather than the card on file).
    paymentMethodId: z.string().startsWith('pm_').optional(),
});
export const ChangePlanResponseSchema = z.object({
    plan: PlanTierSchema,
    effectiveDate: z.string(),
    proration: z.boolean(),
});
export const PortalResponseSchema = z.object({ url: z.string().url() });
//# sourceMappingURL=billing.js.map